import { UiSurface } from "shared/ui/uiSurface"
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { Box, Typography } from "@mui/material"
import { t } from "i18next"
import { FC, useCallback, useMemo } from "react"
import { Virtuoso } from "react-virtuoso"
import { OrderViewModel } from "src/db/Order"
import {
  OrderOverlay,
  SortableItem,
} from "src/features/shared-module/components"
import { appConstants } from "src/features/shared-module/constants"
import { useAppDispatch, useAppSelector } from "../hooks"
import {
  selectedOrderIdsSetSelector,
  toggleSelectOrder,
} from "../slices/dashboard.slice"
import { DropOrderPlaceholder } from "./DropOrderPlaceholder"
import { OrderCard } from "./OrderCard"

const ITEM_HEIGHT = appConstants.orderRawHeight + 8

export const VirtualizedSortableOrderList: FC<{
  items: OrderViewModel[]
  contextId: string
}> = ({ items, contextId }) => {
  const dispatch = useAppDispatch()
  const activeDragItem = useAppSelector(
    (state) => state.dashboard.activeDragItem,
  )
  const selectedIdsSet = useAppSelector(selectedOrderIdsSetSelector)

  const handleToggleSelect = useCallback(
    (orderId: number, shiftKey: boolean) => {
      dispatch(toggleSelectOrder({ orderId, shiftKey }))
    },
    [dispatch],
  )

  const itemIds = useMemo(() => items.map((order) => order.id), [items])

  const renderItem = useCallback(
    (index: number) => {
      const order = items[index]
      if (!order) return null
      return (
        <Box sx={{ pb: 1 }}>
          <SortableItem id={order.id}>
            {({ listeners }: any) => (
              <OrderCard
                listeners={listeners}
                showDivider={!!order.routeSummaryId}
                order={order}
                selectable
                isSelected={selectedIdsSet.has(order.id)}
                onToggleSelect={handleToggleSelect}
              />
            )}
          </SortableItem>
        </Box>
      )
    },
    [items, selectedIdsSet, handleToggleSelect],
  )

  if (items.length === 0) {
    return (
      <>
        <UiSurface id="UI-DSP-020">
          <SortableContext
            id={contextId}
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {activeDragItem ? (
              <SortableItem id={contextId}>
                <DropOrderPlaceholder />
              </SortableItem>
            ) : (
              <Box
                data-testid={`orders-empty-${contextId}`}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 2,
                  borderRadius: 2,
                  border: "1.5px dashed",
                  height: appConstants.orderRawHeight,
                }}
              >
                <Typography fontWeight={600} color="text.secondary">
                  {t("noOrder")}
                </Typography>
              </Box>
            )}
          </SortableContext>
        </UiSurface>
        <OrderOverlay />
      </>
    )
  }

  return (
    <>
      <UiSurface id="UI-DSP-020">
        <SortableContext
          id={contextId}
          items={itemIds}
          strategy={verticalListSortingStrategy}
        >
          <Virtuoso
            totalCount={items.length}
            itemContent={renderItem}
            style={{ height: "100%" }}
            fixedItemHeight={ITEM_HEIGHT}
            overscan={200}
          />
        </SortableContext>
      </UiSurface>
      {/* Outside the marker wrapper: it must stay a direct child of the parent
          Stack, whose spacing gives it its margin (display:contents takes none). */}
      <OrderOverlay />
    </>
  )
}
